import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare, Mail, Building2, User, Clock, RefreshCw,
  CheckCircle2, Filter, Bot, ArrowRight,
} from "lucide-react";

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

function short(id) { return id ? String(id).slice(0, 8) : ""; }

const INTENT_TAG = {
  book_meeting: "bg-purple-100 text-purple-700",
  pricing:      "bg-amber-100 text-amber-700",
  human_needed: "bg-red-100 text-red-700",
  faq:          "bg-cyan-100 text-cyan-700",
  small_talk:   "bg-slate-100 text-slate-600",
  opt_out:      "bg-slate-200 text-slate-500",
};

export default function AdminSbtWhatsAppLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // free-text search
  const [onlyProfiled, setOnlyProfiled] = useState(true);
  const [activeSid, setActiveSid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = () => localStorage.getItem("token");
  const hdr = () => ({ Authorization: `Bearer ${token()}` });

  const loadLeads = async () => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/whatsapp/web-chat/leads?limit=200${onlyProfiled ? "&only_profiled=true" : ""}`;
      const r = await fetch(url, { headers: hdr() });
      if (!r.ok) throw new Error("load failed");
      const j = await r.json();
      setLeads(j.leads || []);
    } catch {
      toast.error("Failed to load widget leads");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (sid) => {
    setActiveSid(sid);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await fetch(`${API}/api/admin/whatsapp/web-chat/leads/${sid}`, { headers: hdr() });
      const j = await r.json();
      setDetail(j);
    } catch {
      toast.error("Failed to load transcript");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { loadLeads(); }, [onlyProfiled]);

  const shown = leads.filter((l) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (
      (l.visitor_name  || "").toLowerCase().includes(q) ||
      (l.visitor_email || "").toLowerCase().includes(q) ||
      (l.visitor_company || "").toLowerCase().includes(q) ||
      (l.last_visitor_msg || "").toLowerCase().includes(q)
    );
  });

  const openWhatsApp = (lead) => {
    // If the lead provided a phone, open a wa.me link — otherwise fall
    // back to opening the WhatsApp Inbox admin tab.
    const phone = (lead.visitor_phone || "").replace(/[^\d+]/g, "");
    if (phone) {
      const url = `https://wa.me/${phone.replace(/^\+/, "")}?text=${encodeURIComponent(`Hi ${lead.visitor_name || ""} — following up from your SBT Research chat.`)}`;
      window.open(url, "_blank", "noopener");
    } else {
      toast.info("Visitor didn't share a phone. Reply via the WhatsApp Inbox tab instead.");
    }
  };

  return (
    <div className="space-y-4" data-testid="sbt-whatsapp-widget-leads">
      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <MessageSquare className="w-4 h-4 text-emerald-600" />
            Widget Leads
            <span className="ml-1 text-xs font-normal text-slate-500">
              ({shown.length}{onlyProfiled ? " with contact info" : " sessions"})
            </span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search name, email, company, message…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-9 w-72"
              data-testid="widget-leads-search"
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none">
              <input
                type="checkbox"
                checked={onlyProfiled}
                onChange={(e) => setOnlyProfiled(e.target.checked)}
                data-testid="widget-leads-only-profiled"
              />
              Only leads with contact info
            </label>
            <Button
              variant="outline" size="sm" onClick={loadLeads} disabled={loading}
              data-testid="widget-leads-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Leads list */}
        <div className="lg:col-span-2 space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="widget-leads-list">
          {loading && (
            <div className="text-sm text-slate-500 p-6 text-center">Loading…</div>
          )}
          {!loading && shown.length === 0 && (
            <div className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-md">
              No widget leads yet. Once a visitor submits the profile card on the marketing site, they&apos;ll appear here.
            </div>
          )}
          {shown.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => loadDetail(l.id)}
              className={`w-full text-left rounded-md border transition p-3 ${
                activeSid === l.id
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
              }`}
              data-testid={`widget-lead-row-${l.id}`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  l.profile_captured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {l.profile_captured ? <CheckCircle2 className="w-4 h-4"/> : <User className="w-4 h-4"/>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {l.visitor_name || <span className="text-slate-400 italic">Anonymous visitor</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3"/> {fmt(l.last_message_at)}
                    </div>
                  </div>
                  {l.visitor_email && (
                    <div className="text-xs text-slate-600 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3"/> {l.visitor_email}
                    </div>
                  )}
                  {l.visitor_phone && (
                    <div className="text-xs text-emerald-700 truncate flex items-center gap-1 font-medium">
                      <MessageSquare className="w-3 h-3"/> {l.visitor_phone}
                    </div>
                  )}
                  {l.visitor_company && (
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <Building2 className="w-3 h-3"/> {l.visitor_company}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {l.last_visitor_msg || <span className="italic">(no messages)</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {l.last_intent && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${INTENT_TAG[l.last_intent] || "bg-slate-100 text-slate-600"}`}>
                        {l.last_intent.replace(/_/g, " ")}
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      {l.total_messages || 0} msgs
                    </span>
                    <span className="text-[10px] text-slate-400 ml-auto">#{short(l.id)}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-3">
          <Card className="min-h-[60vh]">
            <CardContent className="p-4">
              {!activeSid && (
                <div className="flex flex-col items-center justify-center h-[60vh] text-slate-400">
                  <MessageSquare className="w-10 h-10 mb-3 opacity-40"/>
                  <p className="text-sm">Select a lead to view the full chat transcript</p>
                </div>
              )}
              {activeSid && detailLoading && (
                <div className="text-sm text-slate-500 p-6 text-center">Loading transcript…</div>
              )}
              {activeSid && detail && detail.session && (
                <div data-testid="widget-lead-detail">
                  {/* Lead header */}
                  <div className="border-b border-slate-200 pb-3 mb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-base font-bold text-slate-900 truncate">
                          {detail.session.visitor_name || <span className="text-slate-400 italic">Anonymous visitor</span>}
                        </h4>
                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-xs text-slate-600">
                          {detail.session.visitor_email && (
                            <div className="flex items-center gap-1.5 truncate">
                              <Mail className="w-3 h-3 flex-shrink-0"/>
                              <a href={`mailto:${detail.session.visitor_email}`} className="text-cyan-700 hover:underline truncate">
                                {detail.session.visitor_email}
                              </a>
                            </div>
                          )}
                          {detail.session.visitor_phone && (
                            <div className="flex items-center gap-1.5 truncate">
                              <MessageSquare className="w-3 h-3 flex-shrink-0 text-emerald-600"/>
                              <span className="font-medium text-emerald-700">{detail.session.visitor_phone}</span>
                            </div>
                          )}
                          {detail.session.visitor_company && (
                            <div className="flex items-center gap-1.5 truncate">
                              <Building2 className="w-3 h-3 flex-shrink-0"/>{detail.session.visitor_company}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 flex-shrink-0"/>Started {fmt(detail.session.created_at)}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-400">Session</span>#{short(detail.session.id)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <Button
                          size="sm" onClick={() => openWhatsApp(detail.session)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="widget-lead-reply-whatsapp"
                        >
                          Reply on WhatsApp <ArrowRight className="w-3 h-3 ml-1"/>
                        </Button>
                        {detail.session.visitor_email && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => window.open(`mailto:${detail.session.visitor_email}?subject=${encodeURIComponent("Following up from your SBT Research chat")}`, "_blank")}
                            data-testid="widget-lead-reply-email"
                          >
                            Reply via Email
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transcript */}
                  <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="widget-lead-transcript">
                    {(detail.messages || []).length === 0 && (
                      <div className="text-sm text-slate-500 p-4 text-center">No messages in this session yet.</div>
                    )}
                    {(detail.messages || []).map((m) => {
                      const isVisitor = m.direction === "in";
                      return (
                        <div key={m.id} className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                            isVisitor
                              ? "bg-emerald-100 text-slate-900"
                              : "bg-white border border-slate-200 text-slate-900"
                          }`}>
                            <div className="flex items-center gap-1.5 mb-1 text-[10px] text-slate-500 uppercase tracking-wide">
                              {isVisitor ? <User className="w-3 h-3"/> : <Bot className="w-3 h-3"/>}
                              {isVisitor ? (detail.session.visitor_name || "Visitor") : "AI concierge"}
                              {!isVisitor && m.ai_intent && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded normal-case ${INTENT_TAG[m.ai_intent] || "bg-slate-100 text-slate-600"}`}>
                                  {m.ai_intent.replace(/_/g, " ")}
                                </span>
                              )}
                              <span className="ml-auto normal-case">{fmt(m.created_at)}</span>
                            </div>
                            <div className="whitespace-pre-wrap">{m.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
